import { useMemo } from 'react'
import { CompanyType, isUnderServiceAccount } from '@/app/constants/companyType'
import { CompanyDetailsModel } from '@/app/models/companyDetailsModel'
import { useUser } from '@/modules/shared/context/UserContext'
import { useQueryCompanies } from '@/modules/shared/query-client'
import { useQueryMyServiceAccounts } from '@/modules/shared/query-client/useServiceAccountAdmin'

const sameId = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase()

/**
 * The service account (konto usługowe) that bills `companyId`, if any, and whether the
 * current user may open that account's billing (its roster owner or a global admin).
 */
export function useUmbrellaAccount(companyId?: string | null) {
  const { isAdmin } = useUser()
  const companiesQuery = useQueryCompanies({ includeDeleted: false })
  const mine = useQueryMyServiceAccounts()

  const company = useMemo(
    () => companiesQuery.data?.find((c) => sameId(c.id, companyId)) ?? null,
    [companiesQuery.data, companyId],
  )

  // Roster members do not see the account in the company list, so the nested tenant may
  // be missing; their own accounts (from /mine) name the children explicitly.
  const mineAccount = (mine.data ?? []).find((a) =>
    a.children.some((c) => sameId(c.id, companyId)),
  )
  // Someone with a role only on the child sees neither: the list still names the account.
  const umbrella: CompanyDetailsModel | null = isUnderServiceAccount(company)
    ? (company!.tenant as CompanyDetailsModel)
    : mineAccount
      ? {
          id: mineAccount.id,
          companyName: mineAccount.companyName ?? undefined,
          type: CompanyType.SERVICE,
        }
      : company?.isUnderServiceAccount
        ? {
            id: '',
            companyName: company.serviceAccountName ?? '',
            type: CompanyType.SERVICE,
          }
        : null

  const isOwner =
    !!umbrella &&
    (mine.data ?? []).some(
      (a) => sameId(a.id, umbrella.id) && a.isOwner === true,
    )

  return {
    company,
    umbrella,
    canOpenUmbrella: !!umbrella?.id && (isOwner || isAdmin()),
    isOwner,
    // Until both lists arrive a child looks like a standalone company.
    isLoading: companiesQuery.isLoading || mine.isLoading,
  }
}
